import { useState, useCallback } from 'react';
import { fetchAuthSession } from '../auth/gateway';
import { useDevice } from '../contexts/DeviceContext';
import type { PhotoData } from '../types/dashboard';
import { fetchSignedMediaUrl } from '../utils/media';

export const usePhotoModal = () => {
  const { selectedDevice } = useDevice();
  const [selectedPhoto, setSelectedPhoto] = useState<PhotoData | null>(null);
  const [fullPhotoUrl, setFullPhotoUrl] = useState<string | null>(null);
  const [photoLoading, setPhotoLoading] = useState(false);

  const handlePhotoClick = useCallback(async (photo: PhotoData) => {
    if (!selectedDevice) return;
    
    setSelectedPhoto(photo);
    setPhotoLoading(true);
    
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      
      if (!token) {
        console.error('No token for photo request');
        return;
      }
      
      const signedUrl = await fetchSignedMediaUrl(`/api/v1/devices/${selectedDevice.id}/photos/${photo.id}/file`, token);
      const mediaResponse = await fetch(signedUrl);
      if (!mediaResponse.ok) {
        throw new Error('Failed to download photo from signed URL');
      }
      const blob = await mediaResponse.blob();
      const url = URL.createObjectURL(blob);
      setFullPhotoUrl(url);
    } catch (error) {
      console.error('Failed to fetch full photo:', error);
    } finally {
      setPhotoLoading(false);
    }
  }, [selectedDevice]);

  const handleClosePhoto = useCallback(() => {
    if (fullPhotoUrl && fullPhotoUrl.startsWith('blob:')) {
      URL.revokeObjectURL(fullPhotoUrl);
    }
    setSelectedPhoto(null);
    setFullPhotoUrl(null);
  }, [fullPhotoUrl]);

  return {
    selectedPhoto,
    fullPhotoUrl,
    photoLoading,
    handlePhotoClick,
    handleClosePhoto
  };
};
